import { Box, Container, Divider, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography component="h2" variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
  );
}

export function MentionsLegalesPage() {
  return (
    <LegalLayout title="Mentions légales" lastUpdated="2026-05-12">
      <Section title="Éditeur">
        <p>
          Premium Energy — Outil interne de gestion du support client.
          <br />
          Société : à compléter (raison sociale, RCS, capital social, siège).
          <br />
          Directeur de la publication : à compléter.
        </p>
      </Section>
      <Section title="Hébergement">
        <p>
          Hébergeur : OVHcloud.
          <br />
          Adresse : à compléter. Téléphone : à compléter.
        </p>
      </Section>
      <Section title="Propriété intellectuelle">
        <p>
          L&apos;ensemble des contenus de cette application (textes, logos, marques) est la
          propriété de Premium Energy. Toute reproduction non autorisée est interdite.
        </p>
      </Section>
      <Section title="Contact">
        <p>
          Pour toute question :{' '}
          <Link href="mailto:dpo@premium-energy.local">dpo@premium-energy.local</Link>
        </p>
      </Section>
    </LegalLayout>
  );
}

export function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Politique de confidentialité" lastUpdated="2026-05-12">
      <Section title="Responsable du traitement">
        <p>
          Premium Energy traite des données à caractère personnel dans le cadre de la gestion du
          support client. Le délégué à la protection des données (DPO) est joignable à{' '}
          <Link href="mailto:dpo@premium-energy.local">dpo@premium-energy.local</Link>.
        </p>
      </Section>
      <Section title="Données collectées">
        <ul>
          <li>Identité : nom, prénom, e-mail, téléphone (facultatif).</li>
          <li>Identifiants techniques : sessions, journal d&apos;authentification (12 mois).</li>
          <li>
            Contenu fonctionnel : tickets, messages, pièces jointes, références PDL/PCE/contrat.
          </li>
          <li>Données techniques : IP, user-agent (à des fins d&apos;audit de sécurité).</li>
        </ul>
      </Section>
      <Section title="Finalités et bases légales">
        <p>
          Traitement du support client (intérêt légitime et exécution contractuelle), sécurité du
          système (intérêt légitime), conformité réglementaire (obligation légale).
        </p>
      </Section>
      <Section title="Durée de conservation">
        <ul>
          <li>Comptes utilisateurs : pendant la durée de la relation puis 3 ans.</li>
          <li>Tickets résolus : 3 ans à compter de la résolution.</li>
          <li>Journal d&apos;authentification et de sécurité : 12 mois.</li>
          <li>Comptes supprimés : anonymisation immédiate, effacement physique à J+30.</li>
        </ul>
      </Section>
      <Section title="Droits">
        <p>
          Conformément au RGPD, vous disposez d&apos;un droit d&apos;accès, de rectification,
          d&apos;effacement, de limitation, d&apos;opposition et de portabilité. La suppression du
          compte se fait depuis votre profil. Toute autre demande peut être adressée au DPO.
        </p>
      </Section>
      <Section title="Sécurité">
        <p>
          Chiffrement TLS 1.2+ en transit, AES-256 au repos, mot de passe argon2id, 2FA TOTP
          disponible. Antivirus sur les pièces jointes. Pen-test annuel.
        </p>
      </Section>
      <Section title="Sous-traitants">
        <p>Registre des sous-processeurs disponible sur demande auprès du DPO.</p>
      </Section>
    </LegalLayout>
  );
}

export function AccessibilityPage() {
  return (
    <LegalLayout title="Déclaration d'accessibilité" lastUpdated="2026-05-12">
      <Section title="Engagement">
        <p>
          Premium Energy s&apos;engage à rendre cet outil interne accessible conformément au
          Référentiel général d&apos;amélioration de l&apos;accessibilité (RGAA 4).
        </p>
      </Section>
      <Section title="État de conformité">
        <p>
          État actuel : <strong>partiellement conforme</strong>. Un audit externe est planifié avant
          la mise en production initiale. Les résultats et le plan de remédiation seront mis à jour
          ici à l&apos;issue.
        </p>
      </Section>
      <Section title="Tests automatisés">
        <p>
          axe-core est exécuté en intégration continue sur Storybook. Les composants atomiques sont
          conformes WCAG 2.1 AA par défaut.
        </p>
      </Section>
      <Section title="Retour et contact">
        <p>
          Pour signaler une difficulté d&apos;accessibilité, écrivez à{' '}
          <Link href="mailto:accessibilite@premium-energy.local">
            accessibilite@premium-energy.local
          </Link>
          .
        </p>
      </Section>
      <Section title="Voies de recours">
        <p>
          Si une réponse satisfaisante n&apos;est pas obtenue dans un délai d&apos;un mois, il est
          possible de saisir le Défenseur des droits.
        </p>
      </Section>
    </LegalLayout>
  );
}

function LegalLayout({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}) {
  return (
    <Container maxWidth="md">
      <Box component="main" sx={{ py: 6 }}>
        <Link component={RouterLink} to="/" underline="hover" sx={{ display: 'block', mb: 2 }}>
          ← Retour à l&apos;accueil
        </Link>
        <Typography component="h1" variant="h4" gutterBottom>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 4 }}>
          Dernière mise à jour : {lastUpdated}
        </Typography>
        <Divider sx={{ mb: 4 }} />
        {children}
      </Box>
    </Container>
  );
}
